import {Button as ButtonPrimitive} from '@base-ui/react/button'
import {cva, type VariantProps} from 'class-variance-authority'
import {forwardRef} from 'react'

import {cn} from '@utilities/helpers'

const buttonVariants = cva(
    'group/button inline-flex shrink-0 items-center justify-center rounded-lg cursor-pointer data-disabled:cursor-not-allowed font-semibold break-words whitespace-normal transition-all select-none not-data-disabled:focus-visible:shadow-focus-visible [&_svg]:pointer-events-none [&_svg]:shrink-0',
    {
        variants: {
            variant: {
                primary:
                    'bg-primary-600 text-white shadow-xs not-data-disabled:hover:bg-primary-700 not-data-disabled:hover:text-white active:bg-primary-800 not-data-disabled:aria-[current=page]:bg-primary-800 data-disabled:bg-primary-400',
                primaryDanger:
                    'bg-danger-600 text-white shadow-xs not-data-disabled:hover:bg-danger-700 not-data-disabled:hover:text-white active:bg-danger-800 not-data-disabled:aria-[current=page]:bg-danger-800 data-disabled:bg-danger-400',
                primarySuccess:
                    'bg-success-600 text-white shadow-xs not-data-disabled:hover:bg-success-700 not-data-disabled:hover:text-white active:bg-success-800 not-data-disabled:aria-[current=page]:bg-success-800 data-disabled:bg-success-400',
                secondary:
                    'bg-white text-neutral-700 shadow-xs outline outline-1 outline-neutral-300 -outline-offset-1 not-data-disabled:hover:bg-neutral-50 not-data-disabled:hover:text-neutral-700 active:bg-neutral-100 not-data-disabled:aria-[current=page]:bg-neutral-100 data-disabled:text-neutral-400',
                outlinedPrimary:
                    'bg-white text-primary-700 shadow-xs outline outline-1 outline-neutral-300 -outline-offset-1 not-data-disabled:hover:bg-primary-50 not-data-disabled:hover:text-primary-700 active:bg-primary-100 not-data-disabled:aria-[current=page]:bg-primary-100 data-disabled:text-primary-400',
                tertiary:
                    'text-neutral-900 underline not-data-disabled:hover:text-neutral-900 data-disabled:text-neutral-400',
                primaryUnderlined:
                    'text-primary-600 underline not-data-disabled:hover:text-primary-700 data-disabled:text-primary-400',
                ghost: 'bg-white/50 text-neutral-700 not-data-disabled:hover:bg-neutral-50/70 not-data-disabled:hover:text-neutral-700 active:bg-neutral-100/90 not-data-disabled:aria-[current=page]:bg-neutral-100/90 data-disabled:text-neutral-400',
                ghostDanger:
                    'bg-danger-300/60 text-neutral-500 not-data-disabled:hover:bg-danger-400/70 active:bg-danger-500/80 not-data-disabled:aria-[current=page]:bg-danger-500/80 data-disabled:bg-danger-300/30 data-disabled:text-neutral-400',
                ghostSuccess:
                    'bg-success-300/60 text-neutral-500 not-data-disabled:hover:bg-success-400/70 active:bg-success-500/80 not-data-disabled:aria-[current=page]:bg-success-500/80 data-disabled:bg-success-300/30 data-disabled:text-neutral-400',
                ghostWhite:
                    'text-white not-data-disabled:hover:bg-white/10 active:bg-white/20 not-data-disabled:aria-[current=page]:bg-white/20 data-disabled:text-neutral-100',
                brand: 'relative overflow-hidden bg-linear-(--gradient-rausch) text-white disabled:bg-linear-[unset] disabled:bg-tertiary-bg disabled:text-white aria-disabled:bg-linear-[unset] aria-disabled:bg-tertiary-bg aria-disabled:text-white'
            },
            size: {
                xs: 'text-xs',
                sm: 'text-xs',
                md: 'text-sm',
                lg: 'text-md',
                xl: 'text-md'
            },
            shape: {
                rectangle: '',
                square: 'shrink-0',
                text: ''
            }
        },
        compoundVariants: [
            // ---------- rectangle heights + paddings + gaps + svg sizes ----------
            {shape: 'rectangle', size: 'xs', class: "gap-2 px-2.5 py-[5px] [&_svg:not([class*='size-'])]:size-3.5"},
            {shape: 'rectangle', size: 'sm', class: "h-8 gap-1 px-4 [&_svg:not([class*='size-'])]:size-4"},
            {shape: 'rectangle', size: 'md', class: "h-10 gap-1 px-5 [&_svg:not([class*='size-'])]:size-4"},
            {shape: 'rectangle', size: 'lg', class: "h-12 gap-2.5 px-6 [&_svg:not([class*='size-'])]:size-5"},
            {shape: 'rectangle', size: 'xl', class: "h-13 gap-2.5 px-6 [&_svg:not([class*='size-'])]:size-6"},
            // ---------- text shape (no padding, keep gap + svg) ----------
            {shape: 'text', size: 'xs', class: "gap-2 [&_svg:not([class*='size-'])]:size-3.5"},
            {shape: 'text', size: 'sm', class: "gap-1 [&_svg:not([class*='size-'])]:size-4"},
            {shape: 'text', size: 'md', class: "gap-1 [&_svg:not([class*='size-'])]:size-4"},
            {shape: 'text', size: 'lg', class: "gap-2.5 [&_svg:not([class*='size-'])]:size-5"},
            {shape: 'text', size: 'xl', class: "gap-2.5 [&_svg:not([class*='size-'])]:size-6"},
            // ---------- square (fixed box) ----------
            {shape: 'square', size: 'xs', class: "size-7 [&_svg:not([class*='size-'])]:size-3.5"},
            {shape: 'square', size: 'sm', class: "size-8 [&_svg:not([class*='size-'])]:size-4"},
            {shape: 'square', size: 'md', class: "size-10 [&_svg:not([class*='size-'])]:size-4"},
            {shape: 'square', size: 'lg', class: "size-12 [&_svg:not([class*='size-'])]:size-5"},
            {shape: 'square', size: 'xl', class: "size-13 [&_svg:not([class*='size-'])]:size-6"}
        ],
        defaultVariants: {
            variant: 'primary',
            size: 'md',
            shape: 'rectangle'
        }
    }
)

export type ButtonProps = ButtonPrimitive.Props & VariantProps<typeof buttonVariants>
const Button = forwardRef<HTMLButtonElement, ButtonProps>(
    ({className, variant = 'primary', size = 'md', shape = 'rectangle', ...props}, ref) => {
        return (
            <ButtonPrimitive
                ref={ref}
                className={cn(buttonVariants({variant, size, shape, className}))}
                data-slot="button"
                {...props}
            />
        )
    }
)

Button.displayName = 'Button'

export {Button, buttonVariants}
